import { ORPCError } from "@orpc/client";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "@/lib/public-icons";
import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ApplicationSteps } from "@/components/app/patterns/application-steps";
import { ProductDoorLink } from "@/components/app/patterns/product-door-link";
import { orpcPublic } from "@/lib/orpc";
import { unansweredRequired, type ScreeningAnswer } from "@alm/domain";
import { ScreeningQuestionField } from "@/components/app/screening/screening-question-field";
import { JobDetailMeta } from "@/routes/-components/job-detail-meta";
import { JobProse } from "@/routes/-components/job-prose";
import { ApplyConsents } from "../-components/apply-consents";
import { ApplyContactFields } from "../-components/apply-contact-fields";
import { ApplySent } from "../-components/apply-sent";
import { CareersNotFound } from "@/routes/careers/$slug/-components/careers-not-found";
import { CareersShell } from "../-components/careers-shell";

/** What the second step asks for, so finishing this form never reads as finishing. */
function secondStep(form: boolean, documents: boolean): string {
  if (form && documents) {
    return "Two steps: these quick questions, then the job application and your documents. Applying is finished when both are in.";
  }
  if (form) {
    return "Two steps: these quick questions, then the job application. Applying is finished when you send the job application.";
  }
  return "Two steps: these quick questions, then your documents. Applying is finished when they are uploaded.";
}

export const Route = createFileRoute("/careers/$slug/jobs/$jobId")({
  component: JobPage,
});

/** One position, and the form to apply for it. */
function JobPage() {
  const { slug, jobId } = Route.useParams();
  const page = useQuery({
    ...orpcPublic.careers.job.queryOptions({
      input: { careersSlug: slug, jobId },
    }),
    retry: false,
  });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [answers, setAnswers] = useState<Record<string, ScreeningAnswer>>({});
  // The required questions sent blank, named under their control once tried.
  const [blank, setBlank] = useState<Set<string>>(new Set());
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAccount, setHasAccount] = useState(false);
  const [sent, setSent] = useState(false);

  const apply = useMutation(
    orpcPublic.careers.apply.mutationOptions({
      /* Never a sign-in from here: the next page is the same for everybody, and the way into the
         portal goes to the address typed, so it says nothing about whether it has an account. */
      onSuccess: () => setSent(true),
      onError: (failure: Error) => {
        setError(failure.message);
        setHasAccount(failure instanceof ORPCError && failure.code === "CONFLICT");
      },
    }),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setHasAccount(false);
    /* Checked here before the server does, so the candidate is taken to the
       question rather than told at the bottom that something above is missing. */
    const missing = unansweredRequired(questions, answers);
    setBlank(new Set(missing.map((question) => question.id)));
    if (missing.length > 0) {
      const first = document.getElementById(`q-${missing[0]!.id}`);
      first?.scrollIntoView({ block: "center" });
      first?.focus();
      return;
    }
    apply.mutate({
      careersSlug: slug,
      jobId,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      password: password || undefined,
      company: company || undefined,
      screeningAnswers: answers,
      termsAccepted,
      smsConsent,
    });
  };

  if (page.isError) {
    return (
      <CareersShell slug={slug}>
        <CareersNotFound what="Position" />
      </CareersShell>
    );
  }

  const job = page.data?.job;
  const questions = page.data?.screeningQuestions ?? [];
  // This form is the first half of applying when a job application or documents follow it.
  const jobApplication = page.data?.jobApplication === true;
  const applyDocuments = page.data?.applyDocuments ?? 0;
  const nextStep = jobApplication || applyDocuments > 0;

  return (
    <CareersShell facilityName={page.data?.facility.name} slug={slug}>
      <Link
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/80 mb-4 -ml-1 inline-flex min-h-8 items-center gap-1 rounded-md px-1 text-sm outline-hidden focus-visible:ring-[3px] pointer-coarse:min-h-11"
        params={{ slug }}
        to="/careers/$slug"
      >
        <ChevronLeft className="size-4" />
        All open positions
      </Link>

      {page.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="space-y-8">
          <header className="space-y-3">
            <h1 className="text-3xl font-semibold tracking-tight">{job?.title}</h1>
            <JobDetailMeta job={job} />
            {/* The form sits under the whole description; on a phone that is
                several screens down, so the way to it starts here. */}
            {sent ? null : (
              <Button asChild size="lg" className="w-full sm:w-auto">
                <a href="#apply">Apply for this position</a>
              </Button>
            )}
          </header>

          <JobProse body={job?.description} heading="About the role" />
          <JobProse body={job?.responsibilities} heading="What you would do" />
          <JobProse body={job?.requirements} heading="What we are looking for" />

          <Card id="apply" className="scroll-mt-4 py-6">
            <CardContent className="px-6">
              {sent ? (
                <ApplySent jobTitle={job?.title} nextStep={nextStep} />
              ) : (
                <form className="space-y-4" onSubmit={submit}>
                  {nextStep ? (
                    <div className="space-y-3">
                      <ApplicationSteps at="questions" />
                      <h2 className="text-lg font-semibold">Start your application</h2>
                      <p className="text-muted-foreground text-sm">
                        {secondStep(jobApplication, applyDocuments > 0)}
                      </p>
                    </div>
                  ) : (
                    <h2 className="text-lg font-semibold">Apply for this position</h2>
                  )}

                  <ApplyContactFields
                    values={{ firstName, lastName, email, phone, password, company }}
                    onChange={{
                      firstName: setFirstName,
                      lastName: setLastName,
                      email: setEmail,
                      phone: setPhone,
                      password: setPassword,
                      company: setCompany,
                    }}
                  />

                  {questions.length > 0 ? (
                    <div className="space-y-5 border-t pt-4">
                      <h3 className="text-sm font-semibold">A few questions about you</h3>
                      {questions.map((question) => (
                        <ScreeningQuestionField
                          key={question.id}
                          question={question}
                          value={answers[question.id]}
                          error={blank.has(question.id) ? "Please answer this question." : null}
                          onChange={(next) => {
                            setAnswers((previous) => ({ ...previous, [question.id]: next }));
                            setBlank((previous) => {
                              if (!previous.has(question.id)) return previous;
                              const rest = new Set(previous);
                              rest.delete(question.id);
                              return rest;
                            });
                          }}
                        />
                      ))}
                    </div>
                  ) : null}

                  <ApplyConsents
                    facilityName={page.data?.facility.name}
                    termsAccepted={termsAccepted}
                    smsConsent={smsConsent}
                    onTermsChange={setTermsAccepted}
                    onSmsChange={setSmsConsent}
                  />

                  {error ? <p className="text-destructive text-sm">{error}</p> : null}
                  {hasAccount ? (
                    <p className="text-sm">
                      Applied here before?{" "}
                      <ProductDoorLink
                        className="text-primary underline"
                        to="/login"
                        search={{ next: "/applicant-portal" }}
                      >
                        Sign in
                      </ProductDoorLink>{" "}
                      or{" "}
                      <ProductDoorLink className="text-primary underline" to="/forgot-password">
                        reset your password
                      </ProductDoorLink>
                      .
                    </p>
                  ) : null}

                  <Button disabled={apply.isPending || !termsAccepted} type="submit">
                    {apply.isPending
                      ? "Sending…"
                      : jobApplication
                        ? "Continue to the job application"
                        : nextStep
                          ? "Continue to your documents"
                          : "Send application"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </CareersShell>
  );
}
